import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Redirect, Route, useRouteMatch } from "react-router-dom";

import { useQueryString, useSessionExpirationTracking } from "@lib/hooks";

import { useContainer } from "@contexts/container-context";
import { useCurrentUser } from "@src/hooks/queries/account";
import { useIsLoggedIn } from "@src/hooks/queries/account-selectors";
import { useAppStore } from "@src/stores/zustand/app-store";

export default function PrivateRoute({ children, ...rest }) {
  const { queryParams } = useQueryString();
  const isRedirectPath = !!useRouteMatch("/app/redirect");
  const isLoggedIn = useIsLoggedIn();
  const currentUser = useCurrentUser();
  const setRedirect = useAppStore((state) => state.setRedirect);
  const { snowplowTrackingService, trekService, toggles } = useContainer();
  const [_, setRenderCount] = useState(0);

  useEffect(() => {
    if (isRedirectPath) {
      const action = queryParams.get("act");
      const token = queryParams.get("v");
      if (action && token) {
        setRedirect({
          action,
          token,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      trekService
        .getFeatureFlags()
        .then((res) => {
          for (const [key, value] of Object.entries(res ?? {})) {
            // Set toggles like `ceseFinancialLiteracyEnabled`
            toggles[`cese${key.charAt(0).toUpperCase() + key.slice(1)}`] =
              value;
          }
          // Force a re-render to update the toggles
          setRenderCount((prev) => prev + 1);
        })
        .catch((error) => {
          // Flags only widen what CESE surfaces, so the build-time toggles
          // stand when the read fails. Wrapped in an Error because the client
          // rejects with the raw response, which Sentry records with no message
          // or stack; the response is left out of the report since its `config`
          // carries the Authorization header.
          Sentry.captureException(
            new Error(
              `Trek feature flags unavailable: ${
                error?.status ?? error?.message
              }`
            ),
            { extra: { status: error?.status, statusText: error?.statusText } }
          );
        });
    }
  }, [isLoggedIn, toggles, trekService]);

  useSessionExpirationTracking(currentUser, snowplowTrackingService);

  return (
    <Route
      {...rest}
      render={({ location }) =>
        isLoggedIn ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}
